import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import { useBulkResultNotice } from '~/hooks/bidding/useBulkResultNotice'
import {
  type BulkWithdrawRouting,
  useBlockBidWithdrawFailureNotifier,
  useSpotBidWithdrawFailureNotifier,
} from '~/pages/SpotBidding/hooks/useWithdrawFailureNotice'
import type { BlockBidRow } from '~/pages/SpotBidding/models/blockBidRow'
import type { SpotBidRow } from '~/pages/SpotBidding/models/spotBidRow'
import {
  spotBulkWithdrawNoticeItems,
  spotBulkWithdrawNoticeSpec,
} from '~/pages/SpotBidding/models/spotBulkWithdrawNotice'

/**
 * 一覧を読み直すかは一括取下の実行側 (`useBulkWithdrawBidding`) が結果で決める (AC-BSP-116:
 * 内容だけ先に更新されていた札がある時は自動で読み直さない)。 追い始めた時に読み直すと、 その規則を
 * 破るので、 結果トーストの部品からは読み直さない。
 */
const NO_RELOAD = (): Promise<void> => Promise.resolve()

/**
 * 一括取下を送る札 (通常入札は札 id、 ブロック入札はグループ id) の札ごとの通知を、 応答を待つ間
 * 止める。 返した関数に受け付けた札を渡すと、 その札の結果を件数のトーストで追い始め、 止めていた
 * 通知を戻す (受け付けなかった札は札ごとの通知に戻る)。 返した関数は一覧を読み直す前に呼ぶ
 * (受付時刻は、 その時点で最後に観測した一覧の取得時刻になる)。
 */
export type HoldForBulkWithdraw = (
  keys: readonly string[],
) => (acceptedKeys: readonly string[]) => void

export type SpotWithdrawResultNotices = {
  holdForBulkWithdraw: HoldForBulkWithdraw
}

type Params = {
  /** screen の通常入札の札。 */
  bids: readonly SpotBidRow[]
  /** screen のブロック入札の Leg 行。 */
  blockBidRows: readonly BlockBidRow[]
  /** `bids` と `blockBidRows` を取得した時刻 (screen の `dataUpdatedAt`)。 */
  observedAtMs: number
  /** screen を取得できているか (取得前の空の一覧を観測に数えない)。 */
  isObservable: boolean
  isBeforeDeadline: boolean
}

/**
 * スポットの取下の結果の知らせ (トースト)。 1 件ずつの取下の札は札ごとのトースト (AC-BSP-057b)、
 * 一括取下で受け付けた札は件数でまとめたトースト (AC-BSP-116) で知らせ、 一括の札には札ごとの
 * トーストを出さない。
 *
 * 1 回の観測に、 札ごとの通知 (通常入札 → ブロック入札) を当ててから一括の観測を当てる。 一括の
 * 観測は結果が出た札を追跡から外すので、 逆にすると一括の札にも札ごとのトーストが出る。 一括取下の
 * 応答を待つ間 (まだ追跡していない間) も、 送った札には札ごとのトーストを出さない。
 */
export function useSpotWithdrawResultNotices({
  bids,
  blockBidRows,
  observedAtMs,
  isObservable,
  isBeforeDeadline,
}: Params): SpotWithdrawResultNotices {
  const { t } = useTranslation()
  const notifySpot = useSpotBidWithdrawFailureNotifier(isBeforeDeadline)
  const notifyBlock = useBlockBidWithdrawFailureNotifier(isBeforeDeadline)
  const spec = useMemo(
    () => spotBulkWithdrawNoticeSpec(t, isBeforeDeadline),
    [t, isBeforeDeadline],
  )
  const bulk = useBulkResultNotice(spec, { invalidate: NO_RELOAD })
  const held = useRef(new Set<string>())
  const routing = useMemo<BulkWithdrawRouting>(
    () => ({
      isHeld: (key) => held.current.has(key),
      isBulkTracked: bulk.isTracked,
    }),
    [bulk],
  )

  useEffect(() => {
    notifySpot(bids, routing)
    notifyBlock(blockBidRows, routing)
    bulk.observe(
      isObservable
        ? spotBulkWithdrawNoticeItems(bids, blockBidRows)
        : undefined,
      observedAtMs,
    )
  }, [
    bids,
    blockBidRows,
    observedAtMs,
    isObservable,
    notifySpot,
    notifyBlock,
    routing,
    bulk,
  ])

  const holdForBulkWithdraw = useCallback<HoldForBulkWithdraw>(
    (keys) => {
      for (const key of keys) held.current.add(key)
      return (acceptedKeys) => {
        bulk.track(acceptedKeys)
        for (const key of keys) held.current.delete(key)
      }
    },
    [bulk],
  )
  return useMemo(() => ({ holdForBulkWithdraw }), [holdForBulkWithdraw])
}
